/**
 * PlateFipeMatchDialog - Confirmação da correspondência FIPE da placa
 *
 * Quando o provedor de placa devolve mais de uma correspondência FIPE, este
 * modal lista todas as opções (ordenadas por pontuação) para o usuário
 * confirmar qual é o veículo correto antes de preencher o formulário.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 */

import { useEffect, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { PlateFipeCandidate } from '@/hooks/usePlateLookup';

/** Formata valor monetário no padrão brasileiro */
function formatBRL(value: number | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

interface PlateFipeMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plate: string;
  candidates: PlateFipeCandidate[];
  /** Indica que a opção escolhida está sendo resolvida na base FIPE */
  isApplying?: boolean;
  onConfirm: (candidate: PlateFipeCandidate) => void;
}

export function PlateFipeMatchDialog({
  open,
  onOpenChange,
  plate,
  candidates,
  isApplying = false,
  onConfirm,
}: PlateFipeMatchDialogProps) {
  // Índice selecionado — inicia na opção de maior pontuação (primeira da lista)
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (open) setSelected(0);
  }, [open, candidates]);

  const handleConfirm = () => {
    const candidate = candidates[selected];
    if (candidate) onConfirm(candidate);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirme o veículo na tabela FIPE</DialogTitle>
          <DialogDescription>
            A consulta da placa {plate} retornou {candidates.length} correspondências. Selecione a
            que corresponde ao seu veículo.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] pr-2">
          <div className="space-y-2">
            {candidates.map((candidate, index) => {
              const isSelected = index === selected;
              return (
                <button
                  key={`${candidate.fipe_code ?? 'na'}-${index}`}
                  type="button"
                  onClick={() => setSelected(index)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-left transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : 'border-border/60 hover:bg-muted/50',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {[candidate.brand_text, candidate.model_text].filter(Boolean).join(' ') ||
                          'Modelo não informado'}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {[
                          candidate.year ? `Ano ${candidate.year}` : null,
                          candidate.fuel,
                          candidate.fipe_code ? `FIPE ${candidate.fipe_code}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      <p className="mt-1 text-xs font-medium text-foreground">
                        {formatBRL(candidate.value)}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {index === 0 && (
                        <Badge variant="secondary" className="gap-1 font-normal">
                          <Sparkles className="h-3 w-3" />
                          Sugerida
                        </Badge>
                      )}
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={isApplying}>
            {isApplying ? 'Aplicando...' : 'Usar esta opção'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
